import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';

/*
 * Long-form text (project READMEs, blog posts) in the reading style from DESIGN.md:
 * Source Serif body in ink, Barlow headings, loden links, code on surface.
 * react-markdown v10 has no `inline` flag on `code`, so `code` is styled as inline
 * and `pre` resets it for blocks.
 */
const components: Components = {
    h1: ({ children }) => <h2 className="text-3xl mt-12 mb-4">{children}</h2>,
    h2: ({ children }) => <h2 className="text-2xl mt-10 mb-3">{children}</h2>,
    h3: ({ children }) => <h3 className="text-xl mt-8 mb-2">{children}</h3>,
    h4: ({ children }) => <h4 className="text-lg mt-6 mb-2">{children}</h4>,
    p:  ({ children }) => <p className="my-4">{children}</p>,
    ul: ({ children }) => <ul className="my-4 pl-6 list-disc marker:text-contour space-y-1.5">{children}</ul>,
    ol: ({ children }) => <ol className="my-4 pl-6 list-decimal marker:text-muted space-y-1.5">{children}</ol>,
    a:  ({ children, href }) => {
        const external = href?.startsWith('http');
        return (
            <a href={href} className="link" {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                {children}
            </a>
        );
    },
    img: ({ src, alt }) => (
        <img src={src} alt={alt ?? ''} loading="lazy" className="w-full my-8 rounded border border-rule bg-surface" />
    ),
    blockquote: ({ children }) => (
        <blockquote className="my-6 border-l-2 border-contour pl-5 text-muted italic">{children}</blockquote>
    ),
    code: ({ className, children }) => (
        <code className={`font-mono text-[0.88em] bg-surface border border-rule rounded-sm px-1 py-px ${className ?? ''}`}>
            {children}
        </code>
    ),
    pre: ({ children }) => (
        <pre className="my-6 overflow-x-auto rounded border border-rule bg-surface p-4 text-sm leading-relaxed [&_code]:bg-transparent [&_code]:border-0 [&_code]:p-0 [&_code]:text-[1em]">
            {children}
        </pre>
    ),
    hr: () => <hr className="my-10 border-0 border-t border-rule" />,
    table: ({ children }) => (
        <div className="my-6 overflow-x-auto">
            <table className="w-full border-collapse text-base">{children}</table>
        </div>
    ),
    th: ({ children }) => <th className="border-b-2 border-ink text-left font-label font-semibold py-2 pr-4">{children}</th>,
    td: ({ children }) => <td className="border-b border-rule py-2 pr-4 align-top">{children}</td>,
};

interface Props {
    markdown: string;
    className?: string;
}

export function MarkdownRenderer({ markdown, className = '' }: Props) {
    return (
        <div className={`max-w-[42rem] text-ink text-[1.0625rem] leading-[1.7] [&>*:first-child]:mt-0 ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, rehypeHighlight]}
                components={components}
            >
                {markdown}
            </ReactMarkdown>
        </div>
    );
}
